import { useMemo } from "react";
import { useGetCompanySpecificIndustryList, useGetNationalityList } from "@/api/services/global/master";
import { ENROLLMENT_STATUS_LABEL, GENDER_LABEL_BY_KEY, VISA_GENDER } from "@/types/enum";
import { TOption } from "@/types/common";

// irohana visa 取り込みモーダルの「フィルタ」パネルが使う選択肢を集約する。
//  - 分野 / 国籍 はシステムの master データ (/master/*) から取得する。
//  - 在籍状況 / 性別 はアプリ内 enum (label マップ) から導出する定数。
// 値はフィルタフォーム(TBulkSyncFilterForm)が文字列で保持するため、全て String 値で揃える。
export const useBulkSyncFilterMasterData = () => {
  // 分野: visa 検索APIが master ID (特定産業分野) を期待するため、検索バーと同じ master を使う。
  const { companySpecificIndustryOptions, isLoading: isLoadingIndustry } = useGetCompanySpecificIndustryList();
  // 国籍: study の master 国籍一覧から取得する。
  const { nationalityOptions, isLoading: isLoadingNationality } = useGetNationalityList();

  // 分野は ID ではなく名称で検索する (API: industryNames)。value に名称を格納する。
  const industryOptions = useMemo<TOption[]>(
    () => companySpecificIndustryOptions.map((opt) => ({ ...opt, value: String(opt.label) })),
    [companySpecificIndustryOptions],
  );

  // 国籍は ID ではなく名称で検索する (API: nationalityNames)。value に名称を格納する。
  const nationalityNameOptions = useMemo<TOption[]>(
    () => nationalityOptions.map((opt) => ({ ...opt, value: String(opt.label) })),
    [nationalityOptions],
  );

  // 在籍状況 / 性別 は enum 由来の定数 (サーバ取得不要)。
  const enrollmentStatusOptions = useMemo<TOption[]>(
    () => Object.entries(ENROLLMENT_STATUS_LABEL).map(([value, label]) => ({ value, label })),
    [],
  );

  // 性別: visa 検索APIは文字列キー (male / female) を期待する。visa は male / female の2値のみ。
  const genderOptions = useMemo<TOption[]>(
    () => Object.values(VISA_GENDER).map((value) => ({ value, label: GENDER_LABEL_BY_KEY[value] })),
    [],
  );

  return {
    industryOptions,
    nationalityOptions: nationalityNameOptions,
    enrollmentStatusOptions,
    genderOptions,
    isLoadingIndustry,
    isLoadingNationality,
  };
};
